import { useMemo, useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { IconButton } from "@mui/material";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";

import { flattenMasterChildRows, masterKey } from "./budgetMasterRows";

const getRowId = (row) => row.rowId;
const getRowClassName = ({ row }) => (row.kind === "child" ? "budget-master-child" : "");
const isRowSelectable = ({ row }) => row.kind === "master";

/**
 * Master/child grid for the Budget Master and Costs screens. The MIT DataGrid has no detail panel, so rows are
 * flattened: the caller filters and sorts the masters, this pages them, and an expanded master's children follow it
 * on the same page. Columns see both kinds of row: `row.kind` is "master" or "child", and a child's `row.parent` is
 * its master. Other props (cell editing or checkbox selection, for example) go to the DataGrid. Only masters can be
 * checked, and a master stays checked while another page is shown; a master's row id is `master:${id}`.
 */
export default function BudgetMasterGrid({
  masters,
  getChildren,
  columns,
  sortModel,
  onSortModelChange,
  isLoading,
  ...props
}) {
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const { pageSize } = paginationModel;
  const page = Math.min(paginationModel.page, Math.max(0, Math.ceil(masters.length / pageSize) - 1));
  const gridPaginationModel = useMemo(() => ({ page, pageSize }), [page, pageSize]);

  const rows = useMemo(() => {
    const start = page * pageSize;
    return flattenMasterChildRows(masters.slice(start, start + pageSize), expandedIds, getChildren);
  }, [masters, page, pageSize, expandedIds, getChildren]);

  const gridColumns = useMemo(() => {
    const toggle = (id) =>
      setExpandedIds((previous) => {
        const next = new Set(previous);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });

    return [
      {
        field: "expand",
        headerName: "",
        width: 56,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row, tabIndex }) =>
          row.kind === "master" && getChildren(row).length > 0 ? (
            <IconButton
              size="small"
              tabIndex={tabIndex}
              aria-label="Show details"
              aria-expanded={expandedIds.has(masterKey(row))}
              onClick={() => toggle(masterKey(row))}
            >
              {expandedIds.has(masterKey(row)) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
          ) : null,
      },
      ...columns,
    ];
  }, [columns, expandedIds, getChildren]);

  return (
    <TilleyDataGrid
      rows={rows}
      columns={gridColumns}
      getRowId={getRowId}
      getRowClassName={getRowClassName}
      isLoading={isLoading}
      isResponsiveShow={false}
      disableColumnFilter
      disableRowSelectionOnClick
      isRowSelectable={isRowSelectable}
      keepNonExistentRowsSelected
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      paginationMode="server"
      rowCount={masters.length}
      paginationModel={gridPaginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={[10, 25, 50, 100]}
      sx={{
        "& .budget-master-child": { backgroundColor: "action.hover" },
        "& .budget-master-child .MuiDataGrid-cellCheckbox": { visibility: "hidden" },
      }}
      {...props}
    />
  );
}
